import {
  Component,
  Inject,
  OnInit,
  Injector,
  OnDestroy,
  ViewChild,
  AfterViewInit
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { Sort, MatSort } from '@angular/material/sort';
import { MatPaginator } from '@angular/material/paginator';
import { Document, DocumentType } from 'src/app/services/documents/documents';
import { BaseComponent } from 'src/app/common/base/base.component';
import { Observable, Subscription } from 'rxjs';
import { FormStatus } from 'src/app/services/forms/form-status';
import { Checklist } from 'src/app/components/checklists/checklists';
import { PageEvent } from '@angular/material/paginator';

@Component({
  selector: 'app-search-dialog',
  templateUrl: './search-dialog.component.html',
  styleUrls: ['./search-dialog.component.scss']
})
export class SearchDialogComponent extends BaseComponent implements OnInit, OnDestroy, AfterViewInit {

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  loading?: boolean;

  displayedColumns: string[] = ['name', 'locationNames', 'userInfo', 'workRelated'];
  detailRowColumns: string[] = ['detail'];

  isDetailRow = (_: number, row: any) => row?.isDetailRow === true;
  isDataRow = (_: number, row: any) => !row?.isDetailRow;

  forms = [
    this.formTypeEnum.SCF,
    this.formTypeEnum.KE,
    this.formTypeEnum.RSSWA,
    this.formTypeEnum.EBF,
    this.formTypeEnum.PPSTB,
    this.formTypeEnum.ABSI
  ];

  documents?: Document[];
  documents$!: Observable<Document[]>;
  documentsSubs!: Subscription;

  formStatuses?: FormStatus[];
  formStatuses$!: Observable<FormStatus[]>;
  formStatusesSubs!: Subscription;

  checklists?: Checklist[];
  checklists$!: Observable<Checklist[]>;
  checklistsSubs!: Subscription;

  documentTypes?: DocumentType[];
  documentTypes$!: Observable<DocumentType[]>;
  documentTypesSubs!: Subscription;

  dataSource: MatTableDataSource<Document> = new MatTableDataSource<Document>([]);
  years: number[] = [];
  year: number = 0;
  type: number = 0;
  filterValue: string = '';

  documentsFiltered: Document[] = [];
  pagedRows: any[] = [];
  pageSize = 5;
  pageIndex = 0;

  constructor(
    protected override injector: Injector,
    public dialogRef: MatDialogRef<SearchDialogComponent>,
    private router: Router,
    @Inject(MAT_DIALOG_DATA) private dialogData: SearchDialogData | null,
  ) {
    super(injector);
  }

  /**
   * Applies an assistant-provided search phrase before loading the shared document catalogs.
   */
  ngOnInit(): void {
    this.filterValue = this.dialogData?.initialFilter?.trim() ?? '';
    this.loading = true;
    this.loadFormStatuses();
    this.applyCombinedFilter();
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;

    this.dataSource.sortingDataAccessor = (item: Document, property: string): string | number => {
      switch (property) {
        case 'name':
          return item.serialNo ?? '';
        case 'description':
          return item.description ?? '';
        case 'locationNames':
          return this.stripHtml(item.locationNames ?? '');
        case 'workRelated':
          return this.stripHtml(item.gen1 ?? '');
        case 'userInfo':
          return `${item.createdByName ?? ''} ${item.requesterName ?? ''} ${item.createdOn ?? ''} ${item.updatedOn ?? ''}`;
        default:
          return '';
      }
    };
  }

  override ngOnDestroy(): void {
    this.documentsSubs?.unsubscribe();
    this.formStatusesSubs?.unsubscribe();
    this.checklistsSubs?.unsubscribe();
    this.documentTypesSubs?.unsubscribe();
    super.ngOnDestroy();
  }

  loadDocumentTypes() {
    this.documentTypes$ = this.store.select(state => state.DocumentType.data);
    this.documentTypesSubs = this.documentTypes$.subscribe(data => {
      if (data?.length) {
        this.documentTypes = data
          .sort((a, b) => this.utils.sort(a.description, b.description));
      }
    });
  }

  /**
   * Loads searchable documents and reapplies the current filter after asynchronous enrichment.
   */
  loadDocuments() {
    this.documents$ = this.store.select(state => state.Documents.data);
    this.documentsSubs = this.documents$.subscribe(data => {
      if (data?.length) {
        this.documents = this.utils.cloneDeep(data.filter(x => x.serialNo));

        this.documents.forEach(d => {
          d.formStatus = this.formStatuses?.find(s => s.id == d.status) ?? new FormStatus();

          const genObj = d.gen1 ? this.utils.JSONparse(d.gen1) : null;

          const locations = d.locationNames
            ? d.locationNames
              .split(',')
              .map(x => x.trim())
              .filter(Boolean)
            : [];

          const shieldings = (genObj?.Shieldings ?? [])
            .filter((x: any) => x?.name)
            .map((x: any) => x.name);

          let html = '<div class="location-group">';

          // Locations section
          if (locations.length > 0) {
            html += `
              <div class="location-section">
                <div class="location-title bold">Locations:</div>
                <div class="gen-chip-wrap">
                  ${locations.map(l => `<span class="chip">${l}</span>`).join('')}
                </div>
              </div>
            `;
          }

          // Shielding section
          if (shieldings.length > 0) {
            html += `
              <div class="location-section">
                <div class="location-title bold mt-2">Shielding Affected:</div>
                <div class="gen-chip-wrap">
                  ${shieldings.map((s: any) => `<span class="chip">${s}</span>`).join('')}
                </div>
              </div>
            `;
          }

          html += '</div>';

          d.locationNames = html;

          // Remove shielding from Work Related
          d.gen1 = this.formatGen(d.gen1);
        });

        // Preserve an assistant-provided filter when documents finish loading asynchronously.
        this.applyCombinedFilter();
        this.loading = false;
        this.loadChecklists();

        this.years = Array.from(
          new Set(
            this.documents
              ?.map(d => d.createdOn ? new Date(d.createdOn).getFullYear() : undefined)
              .filter((y): y is number => y !== undefined)
          )
        ).sort((a, b) => b - a);
      }
    });
  }

  /**
   * Adds checklist status names without clearing an active assistant-provided search.
   */
  loadChecklists() {
    this.checklists$ = this.store.select(state => state.Checklists.data);
    this.checklistsSubs = this.checklists$.subscribe(data => {
      if (data?.length) {
        this.checklists = data;
        const checklistMap = new Map(
          (this.checklists ?? []).map(c => [c.serialNo, c])
        );

        this.documents
          ?.filter(d => !this.forms.includes(d.type ?? 0))
          .forEach(d => {
            const checklist = checklistMap.get(d.serialNo ?? '');
            if (checklist) {
              d.statusName = checklist.checklistStatus?.name;
              d.formStatus = {
                name: checklist.checklistStatus?.name ?? '',
                color: checklist.checklistStatus?.cssClass ?? ''
              } as FormStatus;
            }
          });

        // Checklist status enrichment must not clear the active text filter.
        this.applyCombinedFilter();
      }
    });
  }

  loadFormStatuses() {
    this.formStatuses$ = this.store.select(state => state.FormStatuses.data);
    this.formStatusesSubs = this.formStatuses$.subscribe(data => {
      if (data?.length) {
        this.formStatuses = this.utils.cloneDeep(data);
        this.loadDocuments();
      }
    });
  }

  applyFilter(value?: string) {
    this.filterValue = (value ?? '').trim().toLowerCase();
    this.applyCombinedFilter();
  }

  filterType(e: any) {
    this.type = e.value;
    this.applyCombinedFilter();
  }

  filterYear(e: any) {
    this.year = e.value;
    this.applyCombinedFilter();
  }

  /**
   * Applies text, year, and type filters while preserving the displayed search phrase.
   */
  private applyCombinedFilter() {
    const text = (this.filterValue ?? '').trim().toLowerCase();
    const year = this.year ?? 0;
    const type = this.type ?? 0;

    this.documentsFiltered = (this.documents ?? []).filter(doc => {
      const matchesText =
        !text ||
        (doc.serialNo?.toLowerCase().includes(text)) ||
        (doc.description?.toLowerCase().includes(text)) ||
        (this.stripHtml(doc.locationNames)?.toLowerCase().includes(text)) ||
        (doc.createdByName?.toLowerCase().includes(text)) ||
        (doc.requesterName?.toLowerCase().includes(text)) ||
        (this.stripHtml(doc.gen1)?.toLowerCase().includes(text)) ||
        (doc.statusName?.toLowerCase().includes(text)) ||
        (doc.formStatus?.name?.toLowerCase().includes(text));

      const matchesYear =
        !year || (doc.createdOn ? new Date(doc.createdOn).getFullYear() === year : false);

      const matchesType =
        !type || doc.type === type;

      return !!matchesText && !!matchesYear && !!matchesType;
    });

    this.pageIndex = 0;
    this.updatePagedRows();

    this.loadDocumentTypes();

    if (this.paginator) {
      this.paginator.length = this.documentsFiltered.length;
      this.paginator.firstPage();
    }
  }

  private updatePagedRows() {
    const start = this.pageIndex * this.pageSize;
    const end = start + this.pageSize;

    const pageDocs = this.documentsFiltered.slice(start, end);

    this.pagedRows = pageDocs.flatMap(doc => [
      doc,
      { ...doc, isDetailRow: true }
    ]);

    this.dataSource.data = this.pagedRows;

    if (this.sort) {
      this.dataSource.sort = this.sort;
    }
  }

  onPageChange(event: PageEvent) {
    this.pageSize = event.pageSize;
    this.pageIndex = event.pageIndex;
    this.updatePagedRows();
  }

  public override stripHtml(value?: string): string {
    if (!value) return '';
    const div = document.createElement('div');
    div.innerHTML = value;
    return div.textContent || div.innerText || '';
  }

  formatGen(data?: string) {
    let result = '';
    if (data) {
      const obj = this.utils.JSONparse(data);
      if (obj) {
        result += this.generateHTML(obj.TypeOfWork, 'Type Of Work');
        result += this.generateHTML(obj.Restrictions, 'Restrictions');
      }
    }
    return result;
  }

  generateHTML(items: any[], title: string) {
    if (!items || items.length === 0) return '';

    const chips = items
      .filter(i => i?.name)
      .map(i => `<span class="chip">${i.name}</span>`)
      .join('');

    return `
    <div class="gen-section">
      <div class="gen-title">${title}:</div>
      <div class="gen-chip-wrap">
        ${chips}
      </div>
    </div>
  `;
  }

  openDocument(e: Document) {
    const route = 'redirect/' + e.serialNo;
    this.router.navigate([route]);
    this.dialogRef.close();
  }
}

export interface SearchDialogData {
  initialFilter?: string;
}
